import React, { useRef, useState, useEffect, useMemo, useCallback } from "react"
import { config as springConfig } from 'react-spring'
import {Transition} from 'react-spring/renderprops'
import Button from '@material-ui/core/Button';
import { navigate } from "gatsby"
import * as d3 from 'd3';
import * as topojson from "topojson-client";
import { useWindowSize, useMousePosition } from '../hooks';

import './leif.scss';

console.log('D3?', d3);
  
function distance(co1, co2) {
  return Math.sqrt((co2[1]-co1[1])*(co2[1]-co1[1]) + (co2[0]-co1[0])*(co2[0]-co1[0]))
}

function closeEnough(co1, co2) {
  return distance(co1, co2) < 5;
}

const LeifMap = ({center, cities, visitedCities, selectCity, selectedCity, transition}) => {
  // console.log('PROPS!', props);
  const canvas = useRef(null),
    { width, height } = useWindowSize(),
    scaleInterpolator = useMemo(() => d3.interpolate(height > 500 ? 500 : height > 400 ? 300: 200, 5000), [height]),
    initialScale =  useMemo(() => scaleInterpolator(Math.pow(1-transition.opacity,4)), [scaleInterpolator, transition.opacity]),
    rotationInterpolator = useMemo(() => d3.geoInterpolate(center.coordinates.map(coordinate => -coordinate), selectedCity ? selectedCity.coordinates.map(coordinate => -coordinate) : center.coordinates.map(coordinate => -coordinate)), [selectedCity && selectedCity.coordinates, center.coordinates]),
    initialRotation = useMemo(() => rotationInterpolator(1-transition.opacity), [rotationInterpolator, transition.opacity]),
    λ = useMemo(() => d3.scalePow().exponent(2)
      .domain([-width/2, width/2])
      .range([initialRotation[0]-10, initialRotation[0]+10]), [width, initialScale, initialRotation]),
    φ = useMemo(() => d3.scalePow().exponent(2)
      .domain([-height/2, height/2])
      .range([initialRotation[1]+10, initialRotation[1]-10]), [ height, initialScale, initialRotation]),
    { x: mouseX, y: mouseY } = useMousePosition(),
    projection = useMemo(() => d3.geoOrthographic()
      .scale(initialScale)
      .translate([5 * width / 8, height / 2])
      .clipAngle(90)
      .rotate([λ(mouseX - width/2), φ(mouseY-height/2)]), [width, height, initialScale, λ, φ, mouseX, mouseY]),
    unvisitedCities = useMemo(() => cities.filter(city => !visitedCities.reduce((acc, visitedCity) => acc || visitedCity.properties.name === city.properties.name, false)), [cities, visitedCities]),
    [features, setFeatures] = useState(null),
    [context, setContext] = useState(null),
    path = useMemo(() => d3.geoPath()
      .projection(projection)
      .context(context), [projection, context]);

  useEffect(() => {
    async function fetchWorld() {
      let world = await (await fetch('/data/world-110m.json')).json();
      console.log('WORLD!', world);
      let globe = { type: "Sphere" },
        land = topojson.feature(world, world.objects.land),
        countries = topojson.feature(world, world.objects.countries).features,
        borders = topojson.mesh(world, world.objects.countries, function(a, b) { return a !== b; });
      setFeatures({
        globe,
        land,
        countries,
        borders
      });
    }

    fetchWorld();
  }, []);
  
  
  useEffect(() => {
    return () => {
      console.log('OUT!', selectedCity)
      if(selectedCity){
        let newVisitedCities = [...visitedCities];
        if(!visitedCities.reduce((acc, visitedCity) => acc || visitedCity.properties.name === selectedCity.properties.name, false)){
          newVisitedCities.push(selectedCity);
        }
        navigate(`/leif-article/${selectedCity.properties.article.toLowerCase()}`, {
          state: {
            leifInteractive: window.location.pathname,
            visitedCities: newVisitedCities
          }
        });
      }
    }
  }, [selectedCity]);

  useEffect(() => {
    if(canvas.current) {
      setContext(canvas.current.getContext('2d'));
    }
  }, [canvas]);

  useEffect(() => {
    if(context && features) {
      const {
        globe,
        land,
        countries,
        borders
      } = features;

      var landColor = "rgba(255,255,255,1.0)";
      var boundaryColor = '#999';
      var oceanColor = '#45e2b4';
      
      context.clearRect(0, 0, width, height);

      context.strokeStyle = boundaryColor
      context.fillStyle = oceanColor
      context.lineWidth = 2
      context.beginPath()
      path(globe)
      context.stroke()
      context.fill();

      context.fillStyle = landColor
      context.beginPath()
      path(land)
      context.fill();
      
      context.fillStyle = "#f00"
      context.beginPath()
      path({
        type:'GeometryCollection',
        geometries:unvisitedCities
      })
      context.fill();

      context.fillStyle = "#00f"
      context.beginPath()
      path({
        type:'GeometryCollection',
        geometries:visitedCities
      })
      context.fill();

      context.strokeStyle = boundaryColor
      context.lineWidth = .5
      context.beginPath()
      path(borders)
      context.stroke();

      // console.log('RENDERED!', width, height);
    }
  }, [context, features, projection, unvisitedCities, visitedCities]);

  let handleClick = useCallback(e => {
    var screenCoordinates = [e.clientX, e.clientY];
    var mapCoordinates = projection.invert(screenCoordinates);
    console.log('CLICK', e.clientX, e.clientY, mapCoordinates);

    var i;
    for(i = 0; i < cities.length; i++){
      let city = cities[i]
      if(closeEnough(mapCoordinates, city.coordinates)) {
        console.log(city.properties.name);
        selectCity(city);
        break;
      }
    }
  }, [cities, projection]);

  return (<canvas ref={canvas} width={width} height={height} onClick={handleClick}/>);
};

const LeifEmbed = ({ selectedCity, setSelectedCity }) => {
  const { width, height } = useWindowSize()
  return (
    <>
      <Button className="leif-back-button" variant="contained" color="primary" onClick={() => setSelectedCity(null)}>Back</Button>
      { selectedCity && <iframe src={`/leif-article/${selectedCity.properties.article.toLowerCase()}`} width={width} height={height}></iframe> }
    </>
  );
}

// export const Leif = props => {
//   const center = useMemo(() => JSON.parse(props.center), [props.center]),
//     cities = useMemo(() => props.points.map(point => ({
//       coordinates: JSON.parse(point.coordinates).coordinates,
//       type: 'Point',
//       properties: {
//         name: point.name,
//         article: point.article
//       }
//     })), [props.points]),
//     [selectedCity, setSelectedCity] = useState(null);

//   return (
//     <div className="leif-wrapper">
//       <Transition
//         items={!!selectedCity}
//         from={{ position: 'absolute', opacity: 0 }}
//         enter={{ opacity: 1 }}
//         leave={{ opacity: 0 }}
//         config={springConfig.molasses}>
//         {toggle =>
//           toggle
//             ? props => <div style={props}><LeifEmbed selectedCity={selectedCity} setSelectedCity={setSelectedCity} /></div>
//             : props => <div style={props}><LeifMap center={center} cities={cities} selectCity={setSelectedCity} selectedCity={selectedCity}/></div>
//         }
//       </Transition>
//     </div>
//   )
// };

export const Leif = props => {
  console.log('PROPS!', props);
  const center = useMemo(() => props.center ? JSON.parse(props.center) : { coordinates: [0,0] }, [props.center]),
    cities = useMemo(() => props.points ? props.points.filter(point => !!point.coordinates).map(point => ({
      coordinates: JSON.parse(point.coordinates).coordinates,
      type: 'Point',
      properties: {
        name: point.name,
        article: point.article
      }
    })) : [], [props.points]),
    visitedCities = useMemo(() => (props.location.state && props.location.state.visitedCities) ? props.location.state.visitedCities : [], [props.location.state]),
    [selectedCity, setSelectedCity] = useState(null),
    selectCity = useCallback(city => {
      setSelectedCity(city);
      // let newVisitedCities = [...visitedCities];
      // if(!visitedCities.reduce((acc, visitedCity) => acc || visitedCity.properties.name === city.properties.name, false)){
      //   newVisitedCities.push(city);
      // }
      // navigate(`/leif-article/${city.properties.article.toLowerCase()}`, {
      //   state: {
      //     leifInteractive: window.location.pathname,
      //     visitedCities: newVisitedCities
      //   }
      // });
    }, [
      // visitedCities
    ]);

  return (
    <div className="leif-wrapper">
      <Transition
        items={!selectedCity}
        from={{ opacity: 0 }}
        enter={{ opacity: 1 }}
        leave={{ opacity: 0 }}
        config={springConfig.molasses}>
        {show => show && (props => <div style={props}><LeifMap transition={props} center={center} cities={cities} visitedCities={visitedCities} selectCity={selectCity} selectedCity={selectedCity}/></div>)}
      </Transition>
    </div>
  )
};